import { z } from 'zod';

import type {
  CamundaFormObject,
  CamundaFormValue,
  CamundaJsonObject,
  CamundaJsonValue,
} from './types';

export interface SerializedMultiFileItem {
  filename: string;
  mimeType?: string;
  tipo: string;
  content?: string;
  contentBase64?: string;
  id?: string;
  documentId?: string;
  documentTitle?: string;
  fileVariableName?: string;
  notes?: string;
  persisted?: boolean;
  revision?: number;
  source?: string;
  metadata?: CamundaJsonObject;
}

export interface ReconstitutedMultiFileItem {
  [key: string]: CamundaFormValue;
  metadata?: CamundaJsonObject;
  file: File;
  tipo: string;
  filename?: string;
  id?: string;
  documentId?: string;
  documentTitle?: string;
  fileVariableName?: string;
  notes?: string;
  persisted?: boolean;
  revision?: number;
  source?: string;
}

export interface HistoricFileReference {
  _camundaType: 'File';
  historicDetailId: string;
  filename: string;
  mimeType: string | null;
  processInstanceId: string;
  taskId: string;
}

export interface HistoricFileRevision extends HistoricFileReference {
  variableName: string;
  revision: number;
  occurredAt: string | null;
  actor: string | null;
}

const camundaJsonValueSchema: z.ZodType<CamundaJsonValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(camundaJsonValueSchema),
  z.record(z.string(), camundaJsonValueSchema),
]));

const serializedMultiFileItemSchema = z.object({
  filename: z.string(),
  mimeType: z.string().optional(),
  tipo: z.string(),
  content: z.string().optional(),
  contentBase64: z.string().optional(),
  id: z.string().optional(),
  documentId: z.string().optional(),
  documentTitle: z.string().optional(),
  fileVariableName: z.string().optional(),
  notes: z.string().optional(),
  persisted: z.boolean().optional(),
  revision: z.number().int().positive().optional(),
  source: z.string().optional(),
  metadata: z.record(z.string(), camundaJsonValueSchema).optional(),
}).catchall(camundaJsonValueSchema);

const SERIALIZED_MULTI_FILE_KNOWN_KEYS = new Set([
  'filename',
  'mimeType',
  'tipo',
  'content',
  'contentBase64',
  'id',
  'documentId',
  'documentTitle',
  'fileVariableName',
  'notes',
  'persisted',
  'revision',
  'source',
  'metadata',
]);

const normalizeSerializedMultiFileItem = <TValue>(value: TValue): SerializedMultiFileItem | null => {
  const parsed = serializedMultiFileItemSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  const metadata: CamundaJsonObject = { ...parsed.data.metadata };
  for (const [key, nestedValue] of Object.entries(parsed.data)) {
    if (!SERIALIZED_MULTI_FILE_KNOWN_KEYS.has(key)) {
      metadata[key] = nestedValue;
    }
  }

  return {
    filename: parsed.data.filename,
    mimeType: parsed.data.mimeType,
    tipo: parsed.data.tipo,
    content: parsed.data.content,
    contentBase64: parsed.data.contentBase64,
    id: parsed.data.id,
    documentId: parsed.data.documentId,
    documentTitle: parsed.data.documentTitle,
    fileVariableName: parsed.data.fileVariableName,
    notes: parsed.data.notes,
    persisted: parsed.data.persisted,
    revision: parsed.data.revision,
    source: parsed.data.source,
    metadata: Object.keys(metadata).length > 0 ? metadata : undefined,
  };
};

const historicFileReferenceSchema = z.object({
  _camundaType: z.literal('File'),
  historicDetailId: z.string().min(1),
  filename: z.string(),
  mimeType: z.union([z.string(), z.null()]),
  processInstanceId: z.string().min(1),
  taskId: z.string().min(1),
});

const historicFileDtoSchema = historicFileReferenceSchema.omit({
  processInstanceId: true,
  taskId: true,
});

const camundaFormValueSchema: z.ZodType<CamundaFormValue> = z.lazy(() => z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.undefined(),
  z.instanceof(File),
  z.instanceof(Blob),
  z.array(camundaFormValueSchema),
  z.record(z.string(), camundaFormValueSchema),
]));

export function isHistoricFileReference<TValue>(value: TValue): value is TValue & HistoricFileReference {
  return historicFileReferenceSchema.safeParse(value).success;
}

export function attachHistoricFileScope<TValue>(
  value: TValue,
  processInstanceId: string,
  taskId: string,
): CamundaFormValue {
  if (value instanceof File || value instanceof Blob) {
    return value;
  }

  const parsedHistoricFile = historicFileDtoSchema.safeParse(value);
  if (parsedHistoricFile.success) {
    const dto = parsedHistoricFile.data;
    return {
      ...dto,
      processInstanceId,
      taskId,
    } satisfies HistoricFileReference;
  }

  if (Array.isArray(value)) {
    return value.map((item) => attachHistoricFileScope(item, processInstanceId, taskId));
  }

  if (value instanceof Object) {
    const scopedObject: CamundaFormObject = {};
    for (const [key, nested] of Object.entries(value)) {
      scopedObject[key] = attachHistoricFileScope(nested, processInstanceId, taskId);
    }
    return scopedObject;
  }

  const parsedValue = camundaFormValueSchema.safeParse(value);
  return parsedValue.success ? parsedValue.data : null;
}

export function isSerializedMultiFileArray<TValue>(
  parsed: TValue,
): parsed is TValue & SerializedMultiFileItem[] {
  if (!Array.isArray(parsed) || parsed.length === 0) {
    return false;
  }
  return parsed.every((item) => {
    const normalizedItem = normalizeSerializedMultiFileItem(item);
    return normalizedItem !== null
      && (normalizedItem.content !== undefined || normalizedItem.contentBase64 !== undefined);
  });
}

export function reconstituteMultiFileItems(
  serializedItems: SerializedMultiFileItem[],
): ReconstitutedMultiFileItem[] {
  return serializedItems.map((item) => {
    const normalizedItem = normalizeSerializedMultiFileItem(item);
    if (!normalizedItem) {
      throw new Error('El elemento de archivo serializado no cumple el contrato de Camunda.');
    }
    const encodedContent = normalizedItem.content ?? normalizedItem.contentBase64;
    if (encodedContent === undefined) {
      throw new Error('El elemento de archivo serializado no incluye contenido binario.');
    }
    const byteCharacters = atob(encodedContent);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let index = 0; index < byteCharacters.length; index += 1) {
      byteNumbers[index] = byteCharacters.charCodeAt(index);
    }
    const file = new File([byteNumbers], normalizedItem.filename, {
      type: normalizedItem.mimeType || 'application/octet-stream',
    });
    return {
      metadata: normalizedItem.metadata,
      file,
      tipo: normalizedItem.tipo,
      filename: normalizedItem.filename,
      id: normalizedItem.id,
      documentId: normalizedItem.documentId,
      documentTitle: normalizedItem.documentTitle,
      fileVariableName: normalizedItem.fileVariableName,
      notes: normalizedItem.notes,
      persisted: normalizedItem.persisted,
      revision: normalizedItem.revision,
      source: normalizedItem.source,
    };
  });
}
